import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { savePromozioni } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelBody, PanelHead, Button, Spinner } from '../components/ui';
import { callFunction } from '../utils/cloudFunctions';

// ─── Helper ───────────────────────────────────────────────────────────────────
function MSIcon({ name, size = 18, color = 'currentColor' }) {
    if (!name) return null;
    return (
        <span className="material-symbols-outlined" style={{
            fontSize: `${size}px`, color, lineHeight: 1, display: 'block',
            fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
            userSelect: 'none',
        }}>{name}</span>
    );
}

const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%',
    boxSizing: 'border-box',
};

const TIPI_SCONTO = [
    { id: 'percentuale', label: '% Percentuale', icon: 'percent' },
    { id: 'fisso', label: '€ Importo fisso', icon: 'euro' },
    { id: 'omaggio', label: '🎁 Omaggio / Info', icon: 'card_giftcard' },
];

const COLORI_PROMO = [
    { bg: '#1f93e0', label: 'Blu oceano' },
    { bg: '#0f6e56', label: 'Verde smeraldo' },
    { bg: '#D85A30', label: 'Arancio tramonto' },
    { bg: '#7F77DD', label: 'Viola' },
    { bg: '#c0392b', label: 'Rosso' },
    { bg: '#BA7517', label: 'Oro' },
];

// ─── Form promozione ──────────────────────────────────────────────────────────
function PromoForm({ promo, onSave, onCancel }) {
    const isNew = !promo.id;
    const [form, setForm] = useState({
        id: promo.id || `p${Date.now()}`,
        titolo: promo.titolo || '',
        descrizione: promo.descrizione || '',
        tipoSconto: promo.tipoSconto || 'percentuale',
        sconto: promo.sconto || '',
        dal: promo.dal || '',
        al: promo.al || '',
        colore: promo.colore || '#1f93e0',
        immagineUrl: promo.immagineUrl || '',
        attiva: promo.attiva !== false,
        monouso: promo.monouso ?? false,
        // Default ON solo per promo nuova: in modifica rispetta la scelta originale.
        // La notifica push parte solo quando la promo è NUOVA nel doc del lido
        // (vedi sendPromoNotification), non a ogni edit della stessa promo.
        inviaNotificaPush: promo.id ? (promo.inviaNotificaPush !== false) : true,
    });

    const setF = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
    const setFB = k => e => setForm(p => ({ ...p, [k]: e.target.checked }));

    return (
        <Panel style={{ marginBottom: '20px' }}>
            <PanelHead
                title={isNew ? 'Nuova promozione' : 'Modifica promozione'}
                subtitle="Visibile agli utenti in app"
            />
            <PanelBody>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Titolo */}
                    <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Titolo *
                        </label>
                        <input
                            placeholder="es. Offerta week-end, Pacchetto famiglia..."
                            value={form.titolo}
                            onChange={setF('titolo')}
                            style={inputStyle}
                        />
                    </div>

                    {/* Descrizione */}
                    <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Descrizione
                        </label>
                        <textarea
                            rows={2}
                            placeholder="es. Prenota entro venerdì e risparmia il 10% su tutti i servizi"
                            value={form.descrizione}
                            onChange={setF('descrizione')}
                            style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
                        />
                    </div>

                    {/* Tipo sconto */}
                    <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Tipo offerta
                        </label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {TIPI_SCONTO.map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => setForm(p => ({ ...p, tipoSconto: t.id }))}
                                    style={{
                                        padding: '6px 14px', borderRadius: 'var(--radius-md)', fontSize: '12.5px',
                                        border: `1px solid ${form.tipoSconto === t.id ? 'var(--ocean)' : 'var(--border-mid)'}`,
                                        background: form.tipoSconto === t.id ? 'rgba(31,147,224,0.15)' : 'transparent',
                                        color: form.tipoSconto === t.id ? 'var(--ocean)' : 'var(--text-soft)',
                                        cursor: 'pointer', fontFamily: 'var(--font-sans)',
                                    }}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Valore sconto (solo se non omaggio) */}
                    {form.tipoSconto !== 'omaggio' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    {form.tipoSconto === 'percentuale' ? 'Sconto (%)' : 'Sconto (€)'}
                                </label>
                                <input
                                    type="number" min="0"
                                    placeholder={form.tipoSconto === 'percentuale' ? '10' : '5.00'}
                                    value={form.sconto}
                                    onChange={setF('sconto')}
                                    style={inputStyle}
                                />
                            </div>
                        </div>
                    )}

                    {/* Date validità */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Dal
                            </label>
                            <input type="date" value={form.dal} onChange={setF('dal')} style={inputStyle} />
                        </div>
                        <div>
                            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Al
                            </label>
                            <input type="date" value={form.al} onChange={setF('al')} style={inputStyle} />
                        </div>
                    </div>

                    {/* Monouso */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)' }}>
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Promo monouso</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Ogni utente può usarla una sola volta</div>
                        </div>
                        <input
                            type="checkbox"
                            checked={form.monouso}
                            onChange={setFB('monouso')}
                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--ocean)' }}
                        />
                    </div>

                    {/* Colore card */}
                    <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Colore card
                        </label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {COLORI_PROMO.map(c => (
                                <button
                                    key={c.bg}
                                    onClick={() => setForm(p => ({ ...p, colore: c.bg }))}
                                    title={c.label}
                                    style={{
                                        width: '32px', height: '32px', borderRadius: '8px',
                                        background: c.bg, border: form.colore === c.bg ? '3px solid #fff' : '3px solid transparent',
                                        cursor: 'pointer', boxShadow: form.colore === c.bg ? `0 0 0 2px ${c.bg}` : 'none',
                                        transition: 'all 0.1s',
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* URL immagine */}
                    <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            URL immagine (opzionale)
                        </label>
                        <input
                            placeholder="https://..."
                            value={form.immagineUrl}
                            onChange={setF('immagineUrl')}
                            style={inputStyle}
                        />
                    </div>

                    {/* Attiva */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={form.attiva}
                            onChange={setFB('attiva')}
                            style={{ width: '16px', height: '16px', accentColor: 'var(--ocean)' }}
                        />
                        <span style={{ fontSize: '13px', color: 'var(--text-soft)' }}>
                            Promozione attiva (visibile in app)
                        </span>
                    </label>

                    {/* Invia notifica push — solo per promo NUOVE (la trigger parte alla prima creazione nel doc) */}
                    {isNew && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={form.inviaNotificaPush}
                                onChange={setFB('inviaNotificaPush')}
                                style={{ width: '16px', height: '16px', accentColor: 'var(--ocean)' }}
                            />
                            <span style={{ fontSize: '13px', color: 'var(--text-soft)' }}>
                                Invia notifica push agli utenti del lido
                            </span>
                        </label>
                    )}

                    {/* Preview card */}
                    {form.titolo && (
                        <div>
                            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Anteprima
                            </label>
                            <PromoCard promo={form} preview />
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
                        <Button variant="primary" onClick={() => onSave(form)} disabled={!form.titolo}>
                            {isNew ? 'Crea promozione' : 'Salva modifiche'}
                        </Button>
                        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
                    </div>
                </div>
            </PanelBody>
        </Panel>
    );
}

// ─── Card promozione ──────────────────────────────────────────────────────────
function PromoCard({ promo, beachCode, onEdit, onDelete, preview = false }) {
    const today = new Date().toISOString().split('T')[0];
    const isScaduta = promo.al && promo.al < today;
    const isInCorso = promo.dal && promo.al && promo.dal <= today && promo.al >= today;
    const [sending, setSending] = useState(false);

    // Re-invio manuale push: il trigger automatico parte solo quando la promo
    // viene aggiunta all'array `promozioni` del lido; per ri-promuovere
    // bisogna chiamare sendManualNotification.
    const handleSendNotification = async () => {
        if (!window.confirm(`Inviare una notifica push agli utenti del lido per "${promo.titolo}"?`)) return;
        setSending(true);
        try {
            const result = await callFunction('sendManualNotification', {
                beachCode,
                type: 'promo',
                id: promo.id,
            });
            alert(`Notifica inviata a ${result.sentTo}/${result.totalDevices} dispositivi.`);
        } catch (err) {
            alert(`Errore invio: ${err.message}`);
        } finally {
            setSending(false);
        }
    };

    const statusLabel = !promo.attiva ? 'Disattivata'
        : isScaduta ? 'Scaduta'
            : isInCorso ? 'In corso'
                : promo.dal > today ? 'Programmata'
                    : 'Attiva';

    const statusColor = !promo.attiva ? '#9ca3af'
        : isScaduta ? '#ef4444'
            : isInCorso ? '#4ade80'
                : '#F5C842';

    return (
        <div style={{
            borderRadius: '14px', overflow: 'hidden',
            border: '1px solid var(--border)',
            marginBottom: preview ? 0 : '10px',
            opacity: (!promo.attiva || isScaduta) ? 0.6 : 1,
        }}>
            {/* Card colorata */}
            <div style={{
                background: promo.immagineUrl
                    ? `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.55)), url(${promo.immagineUrl}) center/cover`
                    : `linear-gradient(135deg, ${promo.colore || '#1f93e0'} 0%, ${promo.colore || '#1f93e0'}cc 100%)`,
                padding: '18px 20px',
                minHeight: '90px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
                            {promo.titolo}
                        </div>
                        {promo.descrizione && (
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
                                {promo.descrizione}
                            </div>
                        )}
                    </div>
                    {promo.tipoSconto !== 'omaggio' && promo.sconto && (
                        <div style={{
                            background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                            borderRadius: '10px', padding: '6px 12px',
                            fontSize: '18px', fontWeight: 800, color: '#fff',
                            marginLeft: '12px', flexShrink: 0,
                        }}>
                            {promo.tipoSconto === 'percentuale' ? `-${promo.sconto}%` : `-€${promo.sconto}`}
                        </div>
                    )}
                </div>
                {(promo.dal || promo.al) && (
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginTop: '8px' }}>
                        {promo.dal && `Dal ${promo.dal}`}
                        {promo.dal && promo.al && ' → '}
                        {promo.al && `al ${promo.al}`}
                    </div>
                )}
            </div>

            {/* Footer con azioni (non in preview) */}
            {!preview && (
                <div style={{
                    padding: '10px 16px',
                    background: 'var(--surface)',
                    display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{statusLabel}</span>
                    </div>
                    {!isScaduta && promo.attiva && (
                        <Button size="sm" variant="ghost" onClick={handleSendNotification} disabled={sending}>
                            {sending ? <Spinner size={12} /> : '📣 Notifica'}
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={onEdit}>Modifica</Button>
                    <Button size="sm" variant="danger" onClick={onDelete}>Elimina</Button>
                </div>
            )}
        </div>
    );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function Promozioni() {
    const { beach, admin, refreshBeach } = useAuth();
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const promozioni = beach?.promozioni || [];

    const handleSave = async (promoAggiornata) => {
        setSaving(true);
        try {
            const nuove = editing?.id && promozioni.find(p => p.id === editing.id)
                ? promozioni.map(p => p.id === promoAggiornata.id ? promoAggiornata : p)
                : [...promozioni, promoAggiornata];
            await savePromozioni(admin.beachCode, nuove);
            await refreshBeach();
            setEditing(null);
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Eliminare questa promozione?')) return;
        await savePromozioni(admin.beachCode, promozioni.filter(p => p.id !== id));
        await refreshBeach();
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Topbar
                title="Promozioni"
                subtitle="Offerte e sconti visibili in app"
                actions={
                    <Button variant="primary" onClick={() => setEditing({})}>
                        + Nuova promozione
                    </Button>
                }
            />

            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                {editing !== null && (
                    <PromoForm
                        promo={editing}
                        onSave={handleSave}
                        onCancel={() => setEditing(null)}
                    />
                )}

                {promozioni.length === 0 && editing === null ? (
                    <Panel>
                        <PanelBody>
                            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                <div style={{ fontSize: '28px', marginBottom: '10px' }}>🎁</div>
                                <div style={{ fontSize: '13px', marginBottom: '16px' }}>
                                    Nessuna promozione configurata.<br />
                                    Crea offerte e sconti per attirare più clienti.
                                </div>
                                <Button variant="primary" onClick={() => setEditing({})}>
                                    + Crea prima promozione
                                </Button>
                            </div>
                        </PanelBody>
                    </Panel>
                ) : (
                    <div>
                        {promozioni.map(p => (
                            <PromoCard
                                key={p.id}
                                promo={p}
                                beachCode={admin.beachCode}
                                onEdit={() => setEditing(p)}
                                onDelete={() => handleDelete(p.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}